import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Dialog,
  InputAdornment,
  IconButton,
  Link,
  useTheme,
  useMediaQuery,
  alpha,
  Snackbar,
  Alert,
  Popover,
} from "@mui/material";
import ReCaptchaService from "./ReCaptchaService";
import CloseIcon from '@mui/icons-material/Close';
import { API_CONFIG } from "../../config/api";
import { Visibility, VisibilityOff, Instagram, Facebook, Telegram, X, YouTube, Android as AndroidIcon, KeyboardArrowDown, KeyboardArrowUp, Search } from "@mui/icons-material";
import ForgotPassword from "./ForgotPassword";
import { login, signUp, fetchUserSession, verifyCaptcha } from "../../services/backendAPI";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { REGISTRATION_URL } from "../../utils/constant";
import brokerConfig from "../../config/brokerConfig";
import countryCodes from "../../data/countryCodes.json";
import useGeoCountry from "../../hooks/useGeoCountry";
import useApkLink from "../../hooks/useApkLink";
import { EmojiFlag, checkEmojiFlagSupport } from "../Common/EmojiFlag";
import DownloadAppModal from "../Common/DownloadAppModal";
import CountryCodePicker from "../Common/CountryCodePicker";


const Login = ({ onLogin, onVerify }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [loginTab, setLoginTab] = useState('clientId'); // 'clientId' | 'phone'
  const [phoneNumber, setPhoneNumber] = useState('');
  // Auto-detects country from user IP; starts with IN (India) as fallback
  const { countryCode, setCountryCode } = useGeoCountry();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isTermsNConditionModalOpen, setIsTermsNConditionModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const apkLink = useApkLink();
  const [countdown, setCountdown] = useState(null);
  const [redirectMessage, setRedirectMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isRecaptchaRequired, setIsRecaptchaRequired] = useState(false);
  const [isVerifyingCaptcha, setIsVerifyingCaptcha] = useState(false);
  const [captchaExecute, setCaptchaExecute] = useState(null);

  const navigate = useNavigate();


  useEffect(() => {
    // Microsoft Clarity script initialization
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "yj5dsain9k");

    // Set data-page attribute for chatbot visibility
    document.body.setAttribute('data-page', 'login');

    const savedUsername = localStorage.getItem("login_username");
    if (savedUsername) {
      setCredentials((prev) => ({
        ...prev,
        username: savedUsername,
      }));
    }

    const checkSession = async () => {
      try {
        const response = await fetchUserSession();
        if (response && response.recaptcha_required) {
          setIsRecaptchaRequired(true);
        } else {
          setIsRecaptchaRequired(false);
        }
      } catch (err) {
        console.error("Session check failed:", err);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSession();
      }
    };

    checkSession();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      document.body.removeAttribute('data-page');
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (countdown !== null) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        handleRedirect();
      }
    }
  }, [countdown]);

  const handleRedirect = () => {
    if (redirectMessage.includes("reset password")) {
      setShowForgotPassword(true);
    } else if (redirectMessage.includes("reset PIN")) {
      // Navigate to reset PIN page
      // TODO: Implement reset PIN page
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "username") {
      localStorage.setItem("login_username", value.trim().toUpperCase());
    }
    setCredentials((prev) => ({
      ...prev,
      [name]: name === "username" ? value.trim().toUpperCase() : value,
    }));

    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // --- Resolve channelId from active tab ---
    let channelId;
    if (loginTab === 'clientId') {
      channelId = credentials.username;
    } else {
      if (!phoneNumber.trim()) {
        setError("Please enter your phone number.");
        return;
      }
      channelId = (countryCode?.dial_code || '') + phoneNumber.trim();
    }

    if (isRecaptchaRequired && brokerConfig.recaptchaSiteKey) {
      if (!captchaExecute) {
        setError("reCAPTCHA failed to load. Please try refreshing the page.");
        return;
      }

      setIsVerifyingCaptcha(true);
      try {
        const token = await captchaExecute("login");
        const verifyRes = await verifyCaptcha(token);

        if (verifyRes.status !== "success") {
          setError("Secured verification failed. Please try again.");
          setIsVerifyingCaptcha(false);
          return;
        }
      } catch (err) {
        console.error("Captcha verification error:", err);
        setError("Verification service unavailable. Please try again.");
        setIsVerifyingCaptcha(false);
        return;
      }
      setIsVerifyingCaptcha(false);
    }

    try {
      const response = await login(channelId, credentials.password);
      console.log("response", response);
      if (response.status === "success") {
        // Store basic auth data
        // localStorage.setItem('token', response.data.auth_token);
        localStorage.setItem("login_id", response.data.login_id);
        if (response.data.last_login === null) {
          localStorage.setItem("first_login", true);
        }
        else {
          localStorage.setItem("first_login", false);
        }


        // Handle password reset requirement
        if (response.data.reset_password) {
          setRedirectMessage("Redirecting to reset password page in");
          setCountdown(3);
          return;
        }

        // Handle 2FA Flow
        if (response.data.reset_two_fa) {
          // Case 1: reset_two_fa: true -> Always force PIN setup regardless of twofa_enabled
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("auth_token", response.data.auth_token);
          localStorage.setItem("2fa_setup_required", "true");
          onLogin();
        } else if (response.data.twofa_enabled) {
          // Case 2: reset_two_fa: false && twofa_enabled: true -> Show screen to enter pin
          localStorage.setItem("twofa_token", response.data.twofa.twofa_token);
          localStorage.setItem(
            "twofa_questions",
            JSON.stringify(response.data.twofa.questions)
          );
          localStorage.setItem("twofa_type", response.data.twofa.type);
          localStorage.setItem("isAuthenticated", "true");
          onLogin();
        } else {
          // Case 3: reset_two_fa: false && twofa_enabled: false -> Normal flow of auth (skip 2FA, go directly to dashboard)
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("auth_token", response.data.auth_token);
          localStorage.setItem("2faVerified", "true");
          onLogin();
          onVerify();
        }
      } else {
        setError(response.message || "Invalid credentials");
        setSnackbarMessage(response.message || "Invalid credentials");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.log("err", err);
      setError("Login failed. Please try again.");
      setSnackbarMessage(err.message || "Login failed. Please try again.");
      setOpenSnackbar(true);
      console.error("Login error:", err);
    }
  };
  const handleSignUpClick = async () => {
    // Preserved original commented code
    // try {
    //   const response = await signUp(); // Make the API call

    //   if (response.status === "success") {
    //     enqueueSnackbar("Sign up initiated successfully!", { variant: "success" });

    //     // Navigate based on the message in the response
    //     if (response.data.step === "send-email-otp" || response.data.message === "send-email-otp") {
    //       navigate("/signup");
    //     } else if (response.data.step === "verify-email-otp") {
    //       navigate("/verify-otp");
    //     } else {
    //       enqueueSnackbar("Unknown step in response.", { variant: "warning" });
    //     }
    //   } else {
    //     enqueueSnackbar(response.message || "Failed to initiate sign-up.", {
    //       variant: "error",
    //     });
    //   }
    // } catch (error) {
    //   enqueueSnackbar(error.message || "An error occurred.", { variant: "error" });
    // }

    // Open registration URL in a new tab
    const signupLink = brokerConfig.signupLink || REGISTRATION_URL;
    window.open(signupLink, "_blank");
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(45deg, ${alpha(
              theme.palette.primary.dark,
              0.1
            )}, ${alpha(theme.palette.background.default, 0.95)})`
            : `linear-gradient(45deg, ${alpha(
              theme.palette.primary.light,
              0.1
            )}, ${alpha(theme.palette.background.default, 0.95)})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: { xs: "1vh", sm: "2vh" },
        pb: { xs: "1vh", sm: "2vh" },
        px: { xs: 2, sm: 3 },
        overflowY: "auto",
        overflowX: "hidden"
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          mb: 1,
          textAlign: 'center'
        }}
      >
        <img
          src={brokerConfig.logoFull}
          alt={brokerConfig.title}
          style={{
            width: brokerConfig.logoDimensions?.login?.width || 120,
            maxHeight: 60,
            height: 'auto',
            filter: theme.palette.mode === "dark" ? "none" : "none",
            objectFit: 'contain'
          }}
        />
      </Box>

      <Paper
        elevation={isMobile ? 2 : 4}
        sx={{
          width: "100%",
          maxWidth: "400px",
          borderRadius: "16px",
          background: theme.palette.background.paper,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2 },
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontWeight: 600,
              letterSpacing: "0.5px",
              color: theme.palette.text.primary,
            }}
          >
            Sign In
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mt: 1 }}
          >
            Sign in to continue to your account
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, sm: 3 },
            pt: 2,
            pb: 2,
            gap: 2,
          }}
        >
          {/* --- Tab Toggle: Client ID/Email vs Phone Number --- */}
          {brokerConfig.showCountryPicker && (
            <Box
              sx={{
                display: 'flex',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(0,0,0,0.06)',
                borderRadius: '10px',
                p: '4px',
                gap: '4px',
                mt: 0,
                mb: 2,
              }}
            >
              <Box
                onClick={() => setLoginTab('clientId')}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 0.75,
                  px: 1,
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                  background: loginTab === 'clientId'
                    ? theme.palette.background.paper
                    : 'transparent',
                  color: loginTab === 'clientId'
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  boxShadow: loginTab === 'clientId'
                    ? '0 1px 4px rgba(0,0,0,0.15)'
                    : 'none',
                }}
              >
                Client ID / Email
              </Box>
              <Box
                onClick={() => setLoginTab('phone')}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 0.75,
                  px: 1,
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                  background: loginTab === 'phone'
                    ? theme.palette.background.paper
                    : 'transparent',
                  color: loginTab === 'phone'
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  boxShadow: loginTab === 'phone'
                    ? '0 1px 4px rgba(0,0,0,0.15)'
                    : 'none',
                }}
              >
                Phone Number
              </Box>
            </Box>
          )}

          {/* --- Conditional Input: Client ID/Email OR Phone Number --- */}
          {loginTab === 'clientId' || !brokerConfig.showCountryPicker ? (
            <TextField
              required
              fullWidth
              id="username"
              label="Client ID or Email"
              name="username"
              autoComplete="username"
              autoFocus
              value={credentials.username}
              onChange={handleChange}
              error={!!error}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {/* Country Code Custom Picker */}
              <CountryCodePicker 
                countryCode={countryCode} 
                onChange={setCountryCode} 
              />
              
              {/* Phone Number Input */}
              <TextField
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                autoComplete="tel"
                autoFocus
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setPhoneNumber(digits);
                  setError('');
                }}
                error={!!error}
                size="small"
                inputProps={{ inputMode: 'numeric', maxLength: 15 }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </Box>
          )}

          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={credentials.password}
            onChange={handleChange}
            error={!!error}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {(error || redirectMessage) && (
            <Box sx={{ textAlign: "center" }}>
              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
              {redirectMessage && (
                <Typography color="primary" variant="body2">
                  {redirectMessage} {countdown} seconds...
                </Typography>
              )}
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isVerifyingCaptcha}
            sx={{
              mt: 0.5,
              py: 1.2,
              fontSize: "0.9375rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              "&:hover": {
                background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          >
            Sign In
          </Button>
          <Typography
            sx={{
              display: "inline",
              mt: 1,
              textAlign: "center"
            }}
            variant="caption"
          >
            {`By signing in, you accept our `}
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsTermsNConditionModalOpen(true);
              }}
              sx={{
                textDecoration: "none",
                color: theme.palette.primary.main,
                "&:hover": {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              Terms and Conditions
            </Link>
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Link
              href="#"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                setShowForgotPassword(true);
              }}
              sx={{
                textDecoration: "none",
                color: theme.palette.primary.main,
                "&:hover": {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              Forgot password?
            </Link>
            <Link
              variant="body2"
              onClick={handleSignUpClick}
              sx={{
                cursor: "pointer",
                textDecoration: "none",
                color: theme.palette.primary.main,
                "&:hover": {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              Sign Up
            </Link>
          </Box>

          {apkLink && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 1.5,
                gap: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: "1.5px",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.78rem",
                }}
              >
                TRADE ON THE GO
              </Typography>
              <Button
                onClick={() => setIsDownloadModalOpen(true)}
                variant="contained"
                startIcon={<AndroidIcon />}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  textTransform: "none",
                  borderRadius: "24px",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  fontSize: "0.95rem",
                  boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: `0px 6px 16px ${alpha(theme.palette.primary.main, 0.45)}`,
                  },
                  transition: "all 0.2s ease-in-out",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1
                }}
              >
                Download Mobile App
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Social Media Links - moved outside card */}
      {brokerConfig.socialLinks && (brokerConfig.socialLinks.instagram || brokerConfig.socialLinks.telegram || brokerConfig.socialLinks.facebook || brokerConfig.socialLinks.twitter || brokerConfig.socialLinks.youtube) && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1.5, display: 'block' }}>
            Follow us for updates
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            {brokerConfig.socialLinks.instagram && (
              <IconButton
                component={Link}
                href={brokerConfig.socialLinks.instagram}
                target="_blank"
                sx={{
                  color: "#E1306C",
                  backgroundColor: alpha("#E1306C", 0.1),
                  "&:hover": { backgroundColor: alpha("#E1306C", 0.2) }
                }}
              >
                <Instagram />
              </IconButton>
            )}
            {brokerConfig.socialLinks.telegram && (
              <IconButton
                component={Link}
                href={brokerConfig.socialLinks.telegram}
                target="_blank"
                sx={{
                  color: "#0088cc",
                  backgroundColor: alpha("#0088cc", 0.1),
                  "&:hover": { backgroundColor: alpha("#0088cc", 0.2) }
                }}
              >
                <Telegram />
              </IconButton>
            )}
            {brokerConfig.socialLinks.facebook && (
              <IconButton
                component={Link}
                href={brokerConfig.socialLinks.facebook}
                target="_blank"
                sx={{
                  color: "#1877F2",
                  backgroundColor: alpha("#1877F2", 0.1),
                  "&:hover": { backgroundColor: alpha("#1877F2", 0.2) }
                }}
              >
                <Facebook />
              </IconButton>
            )}
            {brokerConfig.socialLinks.twitter && (
              <IconButton
                component={Link}
                href={brokerConfig.socialLinks.twitter}
                target="_blank"
                sx={{
                  color: "#000000",
                  backgroundColor: alpha("#000000", 0.1),
                  "&:hover": { backgroundColor: alpha("#000000", 0.2) }
                }}
              >
                <X />
              </IconButton>
            )}
            {brokerConfig.socialLinks.youtube && (
              <IconButton
                component={Link}
                href={brokerConfig.socialLinks.youtube}
                target="_blank"
                sx={{
                  color: "#FF0000",
                  backgroundColor: alpha("#FF0000", 0.1),
                  "&:hover": { backgroundColor: alpha("#FF0000", 0.2) }
                }}
              >
                <YouTube />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Add Snackbar at the end */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 4, py: 2 }}>
        <Typography variant="body2" color="textSecondary" align="center">
          Powered by{' '}
          <Link
            href="https://www.suntrader.site/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontWeight: 600,
              textDecoration: "none",
              color: "primary.main" // Suntrader brand color or primary
            }}
          >
            Suntrader
          </Link>
        </Typography>
        {/* 
        {brokerConfig.recaptchaSiteKey && (
          <Typography 
            variant="caption" 
            color="textSecondary" 
            align="center" 
            sx={{ 
              mt: 1, 
              display: 'block', 
              fontSize: '0.65rem', 
              opacity: 0.7,
              px: 2
            }}
          >
            This site is protected by reCAPTCHA and the Google 
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'inherit', marginLeft: '4px', textDecoration: 'underline' }}>Privacy Policy</a> and 
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: 'inherit', marginLeft: '4px', textDecoration: 'underline' }}>Terms of Service</a> apply.
          </Typography>
        )}
        */}
      </Box>

      {/* ReCaptcha Service Component - Only render if siteKey is available */}
      {brokerConfig.recaptchaSiteKey && (
        <ReCaptchaService onReady={setCaptchaExecute} />
      )}

      {/* Download App Modal */}
      <DownloadAppModal open={isDownloadModalOpen} onClose={() => setIsDownloadModalOpen(false)} apkLink={apkLink} />

      {/* Chatbot Integration removed - now global in App.js */}
      <Dialog
        open={isTermsNConditionModalOpen}
        onClose={() => setIsTermsNConditionModalOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 5 },
            overflow: 'hidden',
            p: 0,
            maxWidth: 500,
            width: '100%',
            boxShadow: 24,
            m: 2,
            background: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: '85vh', sm: '90vh' },
          }
        }}
      >
        {/* Custom Modal Header */}
        <Box
          sx={{
            background: theme => theme.palette.primary.main,
            color: 'white',
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1.5, sm: 2.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            justifyContent: 'space-between',
            borderTopLeftRadius: { xs: 3, sm: 20 },
            borderTopRightRadius: { xs: 3, sm: 20 },
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 700, fontSize: '1.35rem', mb: 0.5 }}>Terms and Conditions</Box>
            <Box sx={{ fontSize: '0.98rem', opacity: 0.85 }}>Please read carefully before proceeding</Box>
          </Box>
          <IconButton
            onClick={() => setIsTermsNConditionModalOpen(false)}
            sx={{ color: 'white', ml: 2, background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.18)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Scrollable PayIn content */}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
              SQUARE OFF RULES AND PROCEDURE
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
              Guidelines and Terms for Account Management and Position Liquidation
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              1. Negative Balance Protection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We ensure your account is protected against negative balances. The step out level is set
              at 30%. If your account balance still becomes negative, we will waive off the negative
              amount.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              2. Order Execution Policy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Limit/Stop orders and Stop Loss/Take Profit (SL/TP) orders are executed based on the
              prevailing bid and ask prices, not the Last Traded Price (LTP). This may result in orders
              being executed at prices higher or lower than recent exchange highs or lows, or in some
              cases, orders may not execute even if there is a new high or low on the exchange due to
              bid or ask price movements.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              3. Payout Delays
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Payouts may occasionally be delayed due to banking server issues. Clients are
              requested to remain patient in such situations.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              4. Account Security and Responsibility
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each client is solely responsible for the security of their account. If you choose to share
              your password with someone else, you do so at your own risk. The company will not be
              held responsible for any claims arising from unauthorised transactions.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              5. Crypto Spot Trading Hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The crypto spot market will be closed for trading daily from 02:30 to 03:30 (GMT/UTC).
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              6. Queries Regarding Square Off or Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If there is any error in auto Square Off/Auto Cut, or if any transaction does not align with
              the expected calculation or opinion, you must contact Customer Support within 15
              minutes. No queries will be entertained after this period.
            </Typography>
          </Box>

          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              TERMS & CONDITIONS
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              01. Liquidation/Square Off/Auto Cut
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Liquidation, Square Off, or Auto Cut will commence when there is a 70% MTM (Mark-to-Market) loss in intraday trading. The account may be liquidated at any level above a 70%
              loss.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              02. Holding Margin Requirement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A 100% holding margin is mandatory to carry forward or hold any position overnight. If
              this margin is not maintained, the position will be liquidated.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              03. Order of Liquidation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              At the time of liquidation, the position with the highest loss among all open positions
              will be liquidated or squared off first.
            </Typography>
            {/* </Box> */}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Login;
